'use client';

import { Heading } from '@app/app/_components/Heading/Heading';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { AccessibilityIcon } from '@app/app/_icons/AccessibilityIcon';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { FontSizeConfigurator } from '@app/app/_widgets/AccessabilityMenu/components/FontSizeConfigurator';
import { HighContrastModeToggle } from '@app/app/_widgets/AccessabilityMenu/components/HighContrastModeToggle';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState, KeyboardEvent, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

interface Props {
    extended?: boolean;
    className?: string;
}

export function AccessibilityMenu({ className, extended = false }: Props) {
    const t = useTranslations();

    const appParameters = useAppParameters();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);

    const handleToggle = useCallback(() => {
        if (!appParameters.settings.general.accessibilityMenu) {
            return;
        }

        setOpen(isOpen => !isOpen);
    }, [appParameters.settings.general.accessibilityMenu]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setOpen(false);

            buttonRef.current?.focus();
        }
    }, []);

    useClickOutside(handleClose, [buttonRef, popupRef]);

    useEffect(() => {
        if (open) {
            popupRef.current?.focus();
        }
    }, [open]);

    return (
        <div className="relative">
            {extended ? (
                <button
                    aria-label={t('general.accessibilitySettings')}
                    ref={buttonRef}
                    className={twMerge('flex w-full items-center justify-between text-left', className)}
                    onClick={handleToggle}>
                    <div className="flex items-center gap-3">
                        <AccessibilityIcon className="size-10 text-neutral-500" />
                        <div className="font-medium">{t('general.accessibilitySettings')}</div>
                    </div>
                    <ArrowRightIcon className="ml-2 size-5" />
                </button>
            ) : (
                <button
                    ref={buttonRef}
                    className={className}
                    onClick={handleToggle}
                    aria-label={t('general.accessibilitySettings')}>
                    <AccessibilityIcon className="size-8 text-neutral-500 xs:size-10" />
                </button>
            )}

            {open && (
                <>
                    <div className="fixed inset-0 z-40 block bg-black opacity-45 md:hidden" />

                    <div
                        ref={popupRef}
                        className={twJoin(
                            'flex select-none flex-col overflow-hidden bg-white',
                            'fixed inset-x-0 bottom-0 z-50 max-h-[90vh] rounded-t-lg',
                            'md:absolute md:bottom-auto md:left-auto md:right-0 md:top-12 md:z-10',
                            'md:min-w-prose md:max-h-[50vh] md:min-w-80',
                            'md:rounded-lg md:shadow-lg',
                            'animate-in slide-in-from-bottom-full',
                            'md:animate-in md:fade-in md:slide-in-from-top-10',
                        )}
                        tabIndex={-1}
                        onKeyDown={handleKeyDown}
                        role="menu"
                        aria-label={t('general.accessibilitySettings')}
                        id="accessibility-menu">
                        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-b-neutral-100 px-6 py-3 md:hidden">
                            <div className="text-2xl font-medium">{t('general.accessibilitySettings')}</div>
                            <button className="p-1" onClick={handleClose}>
                                <CloseIcon className="size-6 text-neutral-500" />
                            </button>
                        </div>
                        <div className="flex flex-col gap-6 overflow-y-auto p-6 max-md:pb-16">
                            <Heading level="h4" className="max-md:hidden">
                                {t('general.accessibilitySettings')}
                            </Heading>

                            <FontSizeConfigurator />

                            <HighContrastModeToggle />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
